import './App.css';
import React, { useEffect, useState } from 'react';
import Router from './router.js';
import { BrowserRouter } from 'react-router-dom';
import { interCeptor } from './helper/index.js';
import { useSelector, useDispatch } from 'react-redux';
import { clearUserData } from './slices/loginSlice.js';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme.js';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'An unexpected error occurred.' }
  }

  componentDidCatch(error, info) {
    console.error('App render error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#d32f2f', marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#555', marginBottom: 24, maxWidth: 480 }}>{this.state.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '10px 28px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const INACTIVITY_LIMIT_MS = 2 * 60 * 1000;
const LOGOUT_LIMIT_MS = 20 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
// Shared-PIN staff pages and public display screens -- not the main CMS login, so the
// CMS lock/logout must never apply.
const PIN_GATED_PATH_PREFIXES = ["/leaderboard", "/dealer-tip", "/dealer-hand", "/bbj-display", "/bbj-rules", "/high-hand-display"];
// CMS pages that skip the fake-error lock page but still get the real inactivity logout.
const NO_FAKE_LOCK_PATH_PREFIXES = ["/bad-beat-jackpot"];

function App() {
  const userInfo = useSelector(state => state.loginSlice)
  const dispatch = useDispatch()
  interCeptor(userInfo.token)

  const [apiLoading, setApiLoading] = useState(false);
  useEffect(() => {
    const handler = (e) => setApiLoading(e.detail.active);
    window.addEventListener("api-loading", handler);
    return () => window.removeEventListener("api-loading", handler);
  }, []);

  // Leaderboard/dealer pages are their own PIN-gated staff flows (not the CMS login) --
  // they must never show the fake-error lock page or get logged out.
  const isPinGatedRoute =
    typeof window !== "undefined" &&
    !!window.location.pathname &&
    PIN_GATED_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));

  // Re-read on every render: sidebar navigation changes the path without remounting App.
  const isNoFakeLockRoute =
    typeof window !== "undefined" &&
    NO_FAKE_LOCK_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));

  const [locked, setLocked] = useState(!isPinGatedRoute && !isNoFakeLockRoute);

  // Auto-lock behind a fake error page after 2 minutes of inactivity
  useEffect(() => {
    if (isPinGatedRoute) return;
    if (locked) return;
    let timer = setTimeout(() => setLocked(true), INACTIVITY_LIMIT_MS);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setLocked(true), INACTIVITY_LIMIT_MS);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer));
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [locked]);

  // Actually logs the session out after 5 minutes of inactivity (separate from the
  // 2-minute fake-lock page above -- this clears the real auth token)
  useEffect(() => {
    if (isPinGatedRoute) return;
    let timer = setTimeout(() => {
      dispatch(clearUserData());
      window.location.href = "/login";
    }, LOGOUT_LIMIT_MS);
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch(clearUserData());
        window.location.href = "/login";
      }, LOGOUT_LIMIT_MS);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer));
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [dispatch]);

  // "x" then "y" key sequence restores the real page
  useEffect(() => {
    let lastKey = "";
    let lastKeyTime = 0;
    const handleUnlockKey = (e) => {
      const now = Date.now();
      if (e.key.toLowerCase() === "y" && lastKey === "x" && now - lastKeyTime < 1000) {
        setLocked(false);
        lastKey = "";
        return;
      }
      lastKey = e.key.toLowerCase();
      lastKeyTime = now;
    };
    window.addEventListener("keydown", handleUnlockKey);
    return () => window.removeEventListener("keydown", handleUnlockKey);
  }, []);

  if (locked && !isPinGatedRoute && !isNoFakeLockRoute) {
    const host = typeof window !== "undefined" ? window.location.hostname : "this site";
    return (
      <div style={{
        minHeight: "100vh", background: "#fff",
        fontFamily: "Arial, sans-serif", color: "#202124",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "10vh 20px",
      }}>
        <div style={{ maxWidth: 560, width: "100%" }}>
          <svg width="44" height="39" viewBox="0 0 44 39" style={{ marginBottom: 24 }}>
            <path fill="#5f6368" d="M22 0C12.6 0 4.7 6.3 1.8 15c-.3.8.2 1.7 1 1.9.8.3 1.7-.2 1.9-1C7.2 8.4 14 3 22 3s14.8 5.4 17.3 12.9c.3.8 1.1 1.3 1.9 1 .8-.2 1.3-1.1 1-1.9C39.3 6.3 31.4 0 22 0z"/>
            <circle cx="22" cy="30" r="4" fill="#5f6368"/>
            <rect x="20.3" y="17" width="3.4" height="9" rx="1.7" fill="#5f6368"/>
          </svg>
          <div style={{ fontSize: 20, fontWeight: 400, lineHeight: 1.4, marginBottom: 16 }}>
            This site can&rsquo;t be reached
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: "#202124", marginBottom: 16 }}>
            <strong>{host}</strong> took too long to respond.
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.8, color: "#202124", marginBottom: 8 }}>
            Try:
          </div>
          <ul style={{ fontSize: 14, lineHeight: 1.8, color: "#202124", margin: "0 0 24px", paddingLeft: 20 }}>
            <li>Checking the connection</li>
            <li>Checking the proxy and the firewall</li>
            <li>Running Windows Network Diagnostics</li>
          </ul>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#fff", color: "#1a73e8", border: "1px solid #dadce0",
              borderRadius: 4, padding: "8px 16px", fontSize: 13.5, fontWeight: 500,
              cursor: "pointer", marginBottom: 24,
            }}
          >
            Reload
          </button>
          <div style={{ fontSize: 12, color: "#5f6368", letterSpacing: 0.2 }}>
            ERR_CONNECTION_TIMED_OUT
          </div>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <div className="App">
          {apiLoading && (
            <LinearProgress
              sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, height: 3 }}
            />
          )}
          <BrowserRouter>
            <Router />
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
